import _ from 'underscore';
import React, { Component } from 'react';
import onClickOutside from 'react-onclickoutside';
import { EntitySelector } from '@bingads-webui-react/entity-selector';
import { FocusLock } from '@bingads-webui-react/focus-lock';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';
import classnames from 'classnames';
import { ListSelector } from '@bingads-webui-react/list-selector';
import { fixSVGClassListSupportForIE } from './shim';
import type {
  DropdownSelectorItem,
  IDropdownSelectorI18n,
  IDropdownSelectorProps,
} from './types';

// Start your code from here for component react-example

interface IDropdownSelectorState {
  isToggled: boolean;
}

type DropdownSelectorDefaultProps = Pick<
  IDropdownSelectorProps,
  | 'pageSize'
  | 'customItems'
  | 'onFetch'
  | 'isDisabled'
  | 'onToggle'
  | 'mode'
  | 'renderItem'
  | 'renderSelectedItem'
  | 'renderEmptyList'
  | 'enableRandomPage'
  | 'iconClass'
  | 'enableArrowKeysSupport'
  | 'customMenuAriaLabel'
  | 'dropdownMenuAriaLabel'
  | 'getDropdownButtonA11yLabel'
  | 'shouldFocusFirstFocusableItemOnMount'
  | 'className'
>;

class DropdownSelector extends Component<IDropdownSelectorProps, IDropdownSelectorState> {
  static defaultProps: DropdownSelectorDefaultProps = {
    pageSize: 10,
    customItems: [],
    onFetch: null,
    isDisabled: false,
    onToggle: () => {},
    mode: 'simple',
    renderItem: (item: DropdownSelectorItem) => (
      <li className="list-selector-option" role="presentation" >
        <span className={item.status ? item.status : ''}>{item.value}</span>
      </li>
    ),
    renderSelectedItem: (item: DropdownSelectorItem) => <span className="truncated">{item.value}</span>,
    renderEmptyList: undefined,
    enableRandomPage: false,
    iconClass: 'glyphicon glyphicon-triangle-bottom',
    enableArrowKeysSupport: false,
    customMenuAriaLabel: '',
    dropdownMenuAriaLabel: '',
    getDropdownButtonA11yLabel: (
      selectedItem: DropdownSelectorItem,
      i18n: IDropdownSelectorI18n
    ) => {
      const templateString = i18n.getString(_TL_('{{itemName}} Selected'));

      return !templateString ? undefined : curlyTemplate(templateString)({
        itemName: selectedItem.value,
      });
    },
    shouldFocusFirstFocusableItemOnMount: false,
    className: undefined,
  };

  state = {
    isToggled: false,
  };

  componentDidMount() {
    fixSVGClassListSupportForIE();
  }

  onSelect = (selectedItem: DropdownSelectorItem) => {
    this.toggle();
    this.props.onSelect(selectedItem);
  };

  handleClickOutside = (e: { stopPropagation: () => void }) => {
    // handle click outside to dismiss menu
    if (this.state.isToggled) {
      this.toggle();
    }
    e.stopPropagation();
  };

  toggle = () => {
    const isToggled = !this.state.isToggled;
    if (_.isFunction(this.props.onToggle)) {
      this.props.onToggle();
    }
    this.setState({ isToggled });
  };

  render() {
    const {
      items,
      selectedItem,
      customItems,
      renderItem,
      iconClass,
      enableArrowKeysSupport,
      i18n,
      customMenuAriaLabel,
      dropdownMenuAriaLabel,
    } = this.props;
    let dropDownMenu;

    if (this.props.mode === 'async') {
      dropDownMenu = (
        <EntitySelector
          pageSize={this.props.pageSize}
          onFetch={this.props.onFetch}
          items={items}
          selectedItem={_.findWhere(items, { id: selectedItem.id })}
          totalCount={this.props.totalCount}
          onSelect={this.onSelect}
          renderItem={renderItem}
          renderEmptyList={this.props.renderEmptyList}
          enableRandomPage={this.props.enableRandomPage}
          i18n={i18n}
          enableArrowKeysSupport={enableArrowKeysSupport}
          ariaLabel={dropdownMenuAriaLabel}
        />
      );
    } else {
      dropDownMenu = (
        <div className="list-selector-container">
          <ListSelector
            renderItem={renderItem}
            renderEmptyList={this.props.renderEmptyList}
            onSelect={this.onSelect}
            data={items}
            selectedItem={_.findWhere(items, { id: selectedItem.id })}
            enableArrowKeysSupport={enableArrowKeysSupport}
            ariaLabel={dropdownMenuAriaLabel}
          />
        </div>
      );
    }

    return (
      <div
        className={classnames(
          'ba-dropdown-selector',
          this.state.isToggled ? 'open' : ''
        )}
      >
        <button
          className={classnames(
            'btn btn-default dropdown-toggle',
            this.props.className
          )}
          type="button"
          disabled={this.props.isDisabled}
          tabIndex={0}
          onClick={this.toggle}
          style={{ display: 'flex', justifyContent: 'space-between' }}
          aria-haspopup
          aria-expanded={this.state.isToggled}
          aria-label={this.props.getDropdownButtonA11yLabel(selectedItem, i18n)}
        >
          {this.props.renderSelectedItem(selectedItem)}
          <div className={classnames('icon-right', iconClass)} />
        </button>
        <div className={classnames('dropdown-menu', 'react-dropdown-menu')} >
          <FocusLock
            onExit={this.handleClickOutside}
            focusFirstItemOnMount={this.props.shouldFocusFirstFocusableItemOnMount}
            isControlledFocusLock
            isFocusLock={this.state.isToggled}
          >
            {customItems.length > 0 && (
              <div className="custom-selector-container">
                <ListSelector
                  renderItem={renderItem}
                  onSelect={this.onSelect}
                  data={customItems}
                  selectedItem={_.findWhere(customItems, { id: selectedItem.id })}
                  enableArrowKeysSupport={enableArrowKeysSupport}
                  ariaLabel={customMenuAriaLabel}
                />
              </div>
            )}
            {dropDownMenu}
          </FocusLock>
        </div>
      </div>
    );
  }
}


export const EnhancedDropdownSelector = onClickOutside(DropdownSelector);
